import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import styles from "./PropertyImageUploadModal.module.css";

const PropertyImageUploadModal = ({ isOpen, onClose, propertyId, onSuccess }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef([]);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file count
    if (selectedFiles.length > 20) {
      toast.error(
        t("image_upload_max_files") || "Maximum 20 images allowed"
      );
      return;
    }

    // Validate file types
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const invalidFiles = selectedFiles.filter(
      (file) => !validTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      toast.error(
        t("image_upload_invalid_type") ||
          "Invalid file type. Only JPEG, JPG, PNG, GIF, and WebP images are allowed."
      );
      return;
    }

    setFiles(selectedFiles);

    // Create preview URLs
    const urls = selectedFiles.map((file) => URL.createObjectURL(file));
    previewUrlsRef.current = urls;
    setPreviewUrls(urls);
  };

  // Remove a file from selection
  const handleRemoveFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(previewUrls[index]);
    
    previewUrlsRef.current = newUrls;
    setFiles(newFiles);
    setPreviewUrls(newUrls);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!files.length || !propertyId) {
      toast.error(
        t("image_upload_no_files") || "Please select at least one image"
      );
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("propertyId", propertyId);

      files.forEach((file) => {
        formData.append("images", file);
      });

      const response = await authFetch(`${BASE_URL}/property/upload-images`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          t("image_upload_success") ||
            `Successfully uploaded ${data.data?.images?.length || files.length} image(s)!`
        );

        // Clean up preview URLs
        previewUrls.forEach((url) => URL.revokeObjectURL(url));

        // Reset state
        setFiles([]);
        setPreviewUrls([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Call success callback to refresh property data
        if (onSuccess) {
          onSuccess();
        }

        // Close modal
        onClose();
      } else {
        toast.error(
          data.error ||
            data.message ||
            (t("image_upload_error") || "Failed to upload images. Please try again.")
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(
        t("image_upload_network_error") ||
          "Network error while uploading. Please check your connection."
      );
    } finally {
      setUploading(false);
    }
  };

  // Clean up preview URLs on unmount only (not on every change, which would revoke still-active URLs)
  React.useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {t("image_upload_title") || "Upload Property Images"}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* File Input */}
          <div className={styles.uploadSection}>
            <label className={styles.fileLabel}>
              {t("image_upload_select_files") || "Select Images (up to 6)"}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                multiple
                onChange={handleFileChange}
                className={styles.fileInput}
              />
            </label>
            <p className={styles.fileHint}>
              {t("image_upload_hint") ||
                "Accepted formats: JPEG, JPG, PNG, GIF, WebP"}
            </p>
          </div>

          {/* Preview Section */}
          {previewUrls.length > 0 && (
            <div className={styles.previewSection}>
              <h3 className={styles.previewTitle}>
                {t("image_upload_preview") || "Preview"} ({previewUrls.length}{" "}
                {t("image_upload_images") || "images"})
              </h3>
              <div className={styles.previewGrid}>
                {previewUrls.map((url, index) => (
                  <div key={index} className={styles.previewItem}>
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className={styles.previewImage}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className={styles.removeButton}
                      title={t("image_upload_remove") || "Remove image"}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && (
            <div className={styles.uploadButtonSection}>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || files.length === 0}
                className={styles.uploadButton}
              >
                {uploading
                  ? t("image_upload_uploading") || "Uploading..."
                  : t("image_upload_submit") || `Upload ${files.length} Image(s)`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyImageUploadModal;
